import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { useNotification } from '../context/NotificationContext';
import '../ChangeLog.css';

const ChangeLogForm = ({ onClose, onSave }) => {
    const { showAlert } = useNotification();
    const [version, setVersion] = useState('');
    const [releaseDate, setReleaseDate] = useState(new Date().toISOString().slice(0, 10));
    const [changes, setChanges] = useState([
        { type: 'New', title: '', items: [''] }
    ]);
    const [saving, setSaving] = useState(false);

    const handleAddChange = () => {
        setChanges([...changes, { type: 'New', title: '', items: [''] }]);
    };

    const handleRemoveChange = (index) => {
        const newChanges = [...changes];
        newChanges.splice(index, 1);
        setChanges(newChanges);
    };

    const handleChangeUpdate = (index, field, value) => {
        const newChanges = [...changes];
        newChanges[index][field] = value;
        setChanges(newChanges);
    };

    const handleAddItem = (changeIndex) => {
        const newChanges = [...changes];
        newChanges[changeIndex].items.push('');
        setChanges(newChanges);
    };

    const handleRemoveItem = (changeIndex, itemIndex) => {
        const newChanges = [...changes];
        newChanges[changeIndex].items.splice(itemIndex, 1);
        setChanges(newChanges);
    };

    const handleItemUpdate = (changeIndex, itemIndex, value) => {
        const newChanges = [...changes];
        newChanges[changeIndex].items[itemIndex] = value;
        setChanges(newChanges);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!version || !releaseDate) {
            showAlert("Please fill in version and date");
            return;
        }

        setSaving(true);
        try {
            // 1. Create Change Log Entry
            const { data: logData, error: logError } = await supabase
                .from('change_logs')
                .insert([{ version, release_date: releaseDate }])
                .select()
                .single();

            if (logError) throw logError;

            // 2. Create Items
            const itemsToInsert = changes.map(change => ({
                change_log_id: logData.id,
                type: change.type,
                title: change.title,
                items: change.items.filter(i => i.trim() !== '') // Remove empty lines
            }));

            const { error: itemsError } = await supabase
                .from('change_log_items')
                .insert(itemsToInsert);

            if (itemsError) throw itemsError;

            showAlert("Change log published successfully!");
            onSave();
            onClose();

        } catch (error) {
            console.error("Error saving change log:", error);
            showAlert("Failed to save change log: " + error.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                {/* Header */}
                <div className="modal-header">
                    <h2 className="modal-title">Post New Release</h2>
                    <button onClick={onClose} className="close-btn">
                        <svg className="w-6 h-6" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="modal-body">
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Version</label>
                            <input id="version" name="version"
                                type="text"
                                value={version}
                                onChange={(e) => setVersion(e.target.value)}
                                className="form-input"
                                placeholder="e.g. V1.0.1"
                            />
                        </div>
                        <div className="form-group">
                            <label>Release Date</label>
                            <input id="releasedate" name="releasedate"
                                type="date"
                                value={releaseDate}
                                onChange={(e) => setReleaseDate(e.target.value)}
                                className="form-input"
                            />
                        </div>
                    </div>

                    <div className="changes-section">
                        <div className="section-header">
                            <h3 className="change-title" style={{ fontSize: '0.875rem' }}>Changes</h3>
                            <button
                                type="button"
                                onClick={handleAddChange}
                                className="add-btn"
                            >
                                + Add Section
                            </button>
                        </div>

                        {changes.map((change, idx) => (
                            <div key={idx} className="change-section">
                                <div className="change-row">
                                    <select
                                        value={change.type}
                                        onChange={(e) => handleChangeUpdate(idx, 'type', e.target.value)}
                                        className="select-input"
                                    >
                                        <option value="New">New</option>
                                        <option value="Update">Update</option>
                                        <option value="Bugfix">Bugfix</option>
                                    </select>
                                    <input id="title" name="title"
                                        type="text"
                                        value={change.title}
                                        onChange={(e) => handleChangeUpdate(idx, 'title', e.target.value)}
                                        placeholder="Section Title (e.g. Dashboard Improvements)"
                                        className="form-input flex-1"
                                    />
                                    <button
                                        onClick={() => handleRemoveChange(idx)}
                                        className="icon-btn"
                                    >
                                        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="details-list">
                                    {change.items.map((item, itemIdx) => (
                                        <div key={itemIdx} className="detail-row">
                                            <span className="detail-bullet"></span>
                                            <input id="item" name="item"
                                                type="text"
                                                value={item}
                                                onChange={(e) => handleItemUpdate(idx, itemIdx, e.target.value)}
                                                placeholder="Detail item..."
                                                className="input-invisible"
                                            />
                                            <button
                                                onClick={() => handleRemoveItem(idx, itemIdx)}
                                                className="remove-item-btn"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => handleAddItem(idx)}
                                        className="add-btn"
                                        style={{ fontSize: '0.75rem', marginLeft: '0.75rem', marginTop: '0.5rem' }}
                                    >
                                        + Add Item
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="modal-footer">
                    <button
                        onClick={onClose}
                        className="btn-secondary"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={saving}
                        className="btn-primary"
                    >
                        {saving ? 'Posting...' : 'Post Release'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChangeLogForm;
